import clsx from "clsx";
import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  isValidElement,
  memo,
  ReactNode,
  useCallback,
  useState,
} from "react";

import Label from "./Label";

const numericRegex = /[^0-9.]/g;

// @ts-ignore
export interface RawTextFieldProps
  extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  placeholder?: string;
  label?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  number?: boolean;
  active?: boolean;
  required?: boolean | string;
  showRequired?: boolean;
  error?: boolean;
  prefixComponent?: () => ReactNode;
  suffixComponent?: (() => React.ReactNode) | React.FC;
  labelRightComponent?: () => ReactNode;
  onClick?: () => void;
  multiline?: boolean;
  pattern?: RegExp;
}

const RawTextField = memo(
  forwardRef<HTMLInputElement, RawTextFieldProps>(
    (
      {
        className,
        labelClassName,
        inputClassName,
        label,
        onChange = () => {},
        autoCapitalize = "none",
        onFocus,
        onBlur,
        number,
        required,
        showRequired = false,
        error,
        maxLength,
        prefixComponent,
        suffixComponent,
        labelRightComponent,
        multiline,
        onClick,
        disabled,
        readOnly,
        pattern,
        ...rest
      },
      ref,
    ) => {
      const [active, setActive] = useState(false);

      const onChangeToNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (pattern) {
          if (value.match(pattern)) {
            e.target.value = value.replace(pattern, "");
            e.currentTarget.value = value.replace(pattern, "");
          } else {
            onChange(e);
          }
        } else {
          if (value.match(numericRegex)) {
            const numericValue = (+value.replace(numericRegex, "")).toString();

            e.target.value = numericValue;
            e.currentTarget.value = numericValue;
          }
          onChange(e);
        }
      };

      const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (number || pattern) {
          onChangeToNumber(e);
        } else {
          onChange(e);
        }
      };

      const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        onFocus?.(e);
        setActive(true);
      };

      const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        onBlur?.(e);
        setActive(false);
      };

      const renderSuffixComponent = useCallback(() => {
        if (!suffixComponent) {
          return null;
        }
        if (isValidElement(suffixComponent)) {
          return suffixComponent({});
        }
        const Component: any = suffixComponent;
        return <Component />;
      }, [suffixComponent]);

      const TextInput = (multiline ? "textarea" : "input") as "input";

      return (
        <div className={className}>
          {label && (
            <Label
              required={showRequired && !!required}
              className={labelClassName}
              rightComponent={labelRightComponent}
            >
              {label}
            </Label>
          )}
          <div
            onClick={onClick}
            className={clsx(
              "flex items-center h-12 px-3 bg-white border border-secondary rounded-md gap-2 duration-300",
              {
                // "!border-primary": active,
                "!border-red": error,
                // "h-[150px] py-2 !items-start": multiline,
                // "!bg-bgSecondary": disabled,
                // "h-auto min-h-[150px]": multiline && disabled,
                "cursor-pointer": onClick,
                "!bg-gray cursor-not-allowed": readOnly,
              },
              inputClassName,
            )}
          >
            {prefixComponent?.()}
            <TextInput
              type="text"
              ref={ref}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={clsx(
                "bg-[transparent] text-16 h-full w-full flex-1 outline-none placeholder:text-secondary caret-black selection:bg-gray resize-none read-only:pointer-events-none",
                {
                  "pointer-events-none": onClick,
                },
              )}
              onChange={handleChange}
              autoCapitalize={autoCapitalize}
              inputMode={number ? "numeric" : undefined}
              maxLength={maxLength ? maxLength : multiline ? 5000 : 128}
              disabled={!!onClick || disabled}
              readOnly={readOnly}
              {...rest}
            />
            {renderSuffixComponent()}
          </div>
        </div>
      );
    },
  ),
);

RawTextField.displayName = "RawTextField";

export default RawTextField;
